import React, {Component} from 'react';
import {Link, withRouter} from 'react-router-dom';
import {AccessGateMessage} from './DetailsAccessGate';
import BackButton from './BackButton';
import Pagination from './Pagination';
import Autocomplete from './Autocomplete';
import {describeParty, partyKey} from './partyFormat';


// masked: for visitors without an active subscription, who see the role and date but not the price or address
const PartySummary = ({party, masked}) => (
  <span className="result-row-body">
    <span className="result-row-title">{party.nameFull}</span>
    <span className="result-row-meta">{describeParty(party, masked)}</span>
  </span>
);

class PersonSearch extends Component {

  constructor() {
    super();
    this.state = JSON.parse(window.localStorage.getItem('partySearch_State')) || {
      searchValue: '',
      errorMessage: null,
      isLoading: false,
      matchingNames: [],
      page: 0,
      hasNext: false,
      hasPrevious: false
    };
  }

  setState(state) {
    window.localStorage.setItem('partySearch_State', JSON.stringify(state));
    super.setState(state);
  }

  handleOnChange = value => {
    this.setState({
      searchValue: value,
      errorMessage: null,
      isLoading: false,
      matchingNames: [],
      page: 0,
      hasNext: false,
      hasPrevious: false}
    );
  };

  fetchNameSuggestions = query => this.props.partyApi.suggestName(query);

  handleSuggestionSelect = party => {
    const canViewDetails = this.props.authenticated && this.props.subscriptionStatus && this.props.subscriptionStatus.status === "active";
    // A masked result (the API didn't confirm the subscription) has no propertyId to link to
    if (canViewDetails && party.propertyId) {
      this.props.history.push(`/transactions?propertyId=${party.propertyId}`);
    } else {
      this.setState({
        searchValue: party.nameFull,
        errorMessage: null,
        isLoading: false,
        matchingNames: [party],
        page: 0,
        hasNext: false,
        hasPrevious: false
      });
    }
  };

  async makeApiCall(searchInput, page = 0) {
    const response = await this.props.partyApi.searchByName(searchInput, page);
    if (!response.ok) {
      this.setState({
        searchValue: searchInput,
        errorMessage: `Search by name failed: ${response.status} ${response.statusText}`,
        isLoading: false,
        matchingNames: [],
        page: 0,
        hasNext: false,
        hasPrevious: false
      })
    } else {
      const jsonData = await response.json();
      if(jsonData.content.length === 0) {
        this.setState({
          searchValue: searchInput,
          errorMessage: `No results found. Please revise your input`,
          isLoading: false,
          matchingNames: [],
          page: 0,
          hasNext: false,
          hasPrevious: false
        })
      }
      this.setState({
        searchValue: searchInput,
        errorMessage: null,
        isLoading: false,
        matchingNames: jsonData.content,
        page: jsonData.page,
        hasNext: jsonData.hasNext,
        hasPrevious: jsonData.hasPrevious
      });
    }
  };

  handleSearch = () => {
    this.setState({
      searchValue: this.state.searchValue,
      errorMessage: null,
      isLoading: true,
      matchingNames: [],
      page: 0,
      hasNext: false,
      hasPrevious: false}
    );
    this.makeApiCall(this.state.searchValue, 0);
  };

  handlePageChange = page => {
    this.setState({isLoading: true});
    this.makeApiCall(this.state.searchValue, page);
  };

  handleClear = () => {
    this.setState({
      searchValue: '',
      errorMessage: null,
      isLoading: false,
      matchingNames: [],
      page: 0,
      hasNext: false,
      hasPrevious: false}
    );
  };

  handleKeyDown = event => {
    if (event.key === 'Enter') {
      this.handleSearch();
    }
  };

  render() {
    const {errorMessage, isLoading, searchValue, matchingNames, page, hasNext, hasPrevious} = this.state;
    const canViewDetails = this.props.authenticated && this.props.subscriptionStatus && this.props.subscriptionStatus.status === "active";

    return (
      <div className="app">
        {this.props.navbar}
        <div className="page-container">
          <BackButton/>
          <span className="page-eyebrow">Person search</span>
          <h2 className="page-title">Search by name</h2>
          <p className="page-subtitle">
            Enter the name of the person you're searching for. If you don't get a match with the
            full name, try entering just the first or last name.
          </p>
          <p className="page-subtitle">
            Each result is a name as it appears on one recorded transaction. The same name on two
            transactions may belong to different people.
          </p>

          {errorMessage &&
            <div className="alert callout callout-warning">{errorMessage}</div>
          }

          <Autocomplete
            name="text"
            placeholder="e.g. Jane Doe"
            value={searchValue}
            onChange={this.handleOnChange}
            onKeyDown={this.handleKeyDown}
            fetchSuggestions={this.fetchNameSuggestions}
            getSuggestionKey={partyKey}
            renderSuggestion={party => <PartySummary party={party} masked={!canViewDetails}/>}
            onSelect={this.handleSuggestionSelect}
          />

          <div className="search-actions">
            <button className="btn-brand btn-brand-primary" onClick={this.handleSearch}>Search</button>
            <button className="btn-brand btn-brand-ghost" onClick={this.handleClear}>Clear</button>
          </div>

          {isLoading &&
            <div className="loading-row"><span className="spinner"/> Searching...</div>
          }

          {matchingNames && matchingNames.length > 0 &&
            <AccessGateMessage authenticated={this.props.authenticated} subscriptionStatus={this.props.subscriptionStatus}/>
          }

          {matchingNames && matchingNames.length > 0 &&
            <div className="result-list">
              {matchingNames.map((party, index) => (
                canViewDetails && party.propertyId ?
                  <Link key={`${partyKey(party)}-${index}`} className="result-row is-link" to={`/transactions?propertyId=${party.propertyId}`}>
                    <PartySummary party={party}/>
                    <svg className="result-row-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                  :
                  <div key={`${partyKey(party)}-${index}`} className="result-row">
                    <PartySummary party={party} masked/>
                  </div>
              ))}
            </div>
          }

          {matchingNames && matchingNames.length > 0 &&
            <Pagination page={page} hasNext={hasNext} hasPrevious={hasPrevious} onPageChange={this.handlePageChange}/>
          }
        </div>
      </div>
    );
  }
}

export default withRouter(PersonSearch);
